
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import AuthModal from '../auth/AuthModal';

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { path: '/jobs', label: 'Find Jobs' },
    { path: '/company-reviews', label: 'Company Reviews' },
    { path: '/salary-guide', label: 'Salary Guide' },
    { path: '/why-zing', label: 'Why Choose Zing' },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#1c1c1c] shadow-lg border-b border-[#333333]' : 'bg-[#1c1c1c] border-b border-[#333333]'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center space-x-2 group">
                <div className="w-10 h-10 bg-gradient-to-br from-[#6366f1] to-[#8b5cf6] rounded-lg flex items-center justify-center transform group-hover:scale-105 transition-transform">
                  <i className="ri-flashlight-fill text-white text-xl"></i>
                </div>
                <span className="text-2xl font-bold text-white">Zing<span className="text-[#6366f1]">Jobs</span></span>
              </Link>
            </div>

            {/* Center: Navigation Links (Desktop) */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors relative pb-1 whitespace-nowrap cursor-pointer ${
                    isActive(link.path)
                      ? 'text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-[#6366f1]'
                      : 'text-[#a0a0a0] hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right: Auth & CTA */}
            <div className="hidden md:flex items-center space-x-4">
              {user ? (
                <>
                  <span className="text-sm text-[#a0a0a0]">
                    {user.email}
                  </span>
                  <button
                    onClick={handleSignOut}
                    className="text-sm font-medium text-[#a0a0a0] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setAuthMode('signin');
                      setShowAuthModal(true);
                    }}
                    className="text-sm font-medium text-[#a0a0a0] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => {
                      setAuthMode('signup');
                      setShowAuthModal(true);
                    }}
                    className="text-sm font-medium text-[#a0a0a0] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Create Account
                  </button>
                </>
              )}
              <Link
                to="/employer"
                className="px-6 py-2 bg-[#6366f1] text-white text-sm font-semibold rounded hover:bg-[#5558e3] transition-colors whitespace-nowrap cursor-pointer"
              >
                Post a Job
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-[#a0a0a0] hover:text-white transition-colors cursor-pointer"
              >
                <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-[#1c1c1c] border-t border-[#333333]">
            <div className="px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block py-2 text-sm font-medium transition-colors cursor-pointer ${
                    isActive(link.path) ? 'text-white' : 'text-[#a0a0a0] hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-3 border-t border-[#333333] space-y-3">
                {user ? (
                  <>
                    <div className="text-sm text-[#a0a0a0]">{user.email}</div>
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMenuOpen(false);
                      }}
                      className="block w-full text-left py-2 text-sm font-medium text-[#a0a0a0] hover:text-white transition-colors cursor-pointer"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setAuthMode('signin');
                        setShowAuthModal(true);
                        setIsMenuOpen(false);
                      }}
                      className="block w-full text-left py-2 text-sm font-medium text-[#a0a0a0] hover:text-white transition-colors cursor-pointer"
                    >
                      Sign In
                    </button>
                    <button
                      onClick={() => {
                        setAuthMode('signup');
                        setShowAuthModal(true);
                        setIsMenuOpen(false);
                      }}
                      className="block w-full text-left py-2 text-sm font-medium text-[#a0a0a0] hover:text-white transition-colors cursor-pointer"
                    >
                      Create Account
                    </button>
                  </>
                )}
                <Link
                  to="/employer"
                  onClick={() => setIsMenuOpen(false)}
                  className="block w-full text-center px-6 py-2 bg-[#6366f1] text-white text-sm font-semibold rounded hover:bg-[#5558e3] transition-colors cursor-pointer"
                >
                  Post a Job
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          mode={authMode}
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => {
            setShowAuthModal(false);
          }}
        />
      )}
    </>
  );
}
